import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import Button from 'components/helpers/Button';
import { ButtonVariant } from 'components/helpers/Button/WithButton';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { TextWithOptionalLineBreaks } from 'components/helpers/Heading';
import HeadingTag from 'components/helpers/HeadingTag';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { useIsNRC } from 'lib/util/site-configuration';
import { classnames } from 'tailwindcss-classnames';

export type PageHeroProps = Sitecore.Override.ComponentBase &
  Components.Heroes.PageHero.Fields.PageHero;
const PageHero = ({ fields }: PageHeroProps) => {
  const isExperienceEditor = useExperienceEditor();
  const isNrc = useIsNRC();
  if (!fields) {
    return <MissingDataSource />;
  }

  const heroGradient = classnames('m-0', 'md:w-[56%]', 'md:bg-gradient-to-r');

  const noImg = classnames('overflow-hidden', 'bg-blue-navy', 'w-full');

  const chkImg = fields.image.value?.src ? heroGradient : noImg;

  const cta2TypeField = fields.cta2Type.value;
  const cta2Type: ButtonVariant =
    cta2TypeField === 'Default' || cta2TypeField.trim().length === 0
      ? 'secondary'
      : (cta2TypeField.toLowerCase() as ButtonVariant);

  return (
    <div
      className={classnames(
        'page-hero-container',
        'overflow-hidden',
        'max-w-screen-2xl',
        'mx-auto',
        'w-full',
        'relative',
        'left-0',
        'right-0',
        { 'theme-navy-blue': isNrc }
      )}
      data-component="authorable/Heroes/page-hero"
    >
      <div className={classnames('page-hero-img')}>
        <ImageWrapper field={fields.image} priority layout="intrinsic" />
      </div>
      <div className={classnames('page-hero-overlay', chkImg)}>
        <div
          className={classnames('page-hero-content', 'bg-blue-navy', 'md:bg-opacity-0', 'w-full')}
        >
          <div>
            <HeadingTag
              headingLevel="h1"
              className={classnames('text-white', 'heading-xl', 'line-clamp-2')}
            >
              <TextWithOptionalLineBreaks tag="" field={fields.heroHeadline} />
            </HeadingTag>
            <RichTextA11yWrapper
              className={classnames('text-white', 'text-body', 'line-clamp-4')}
              field={fields.description}
            />
          </div>
          {isExperienceEditor || fields.cta1.value.href || fields.cta2.value.href ? (
            <div className={classnames('cta-container')}>
              <Button linkField={fields.cta1} className={classnames('mr-6')} />
              <Button
                linkField={fields.cta2}
                className={classnames('mt-4', 'md:mt-0')}
                variant={cta2Type}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageHero;
